import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Empty, Progress, Row, Select, Space, Spin, Table, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import PageLayout from '../components/PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import { getJourneyFunnel } from '../apiCalls';

const { Title, Text } = Typography;

const getCurrentPeriodKey = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const createPeriodOptions = () => Array.from({ length: 12 }, (_, index) => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() - index);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return {
        value,
        label: date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
    };
});

const formatPercent = (value) => (value == null ? '—' : `${value}%`);

const FunnelMetric = ({ label, value, color }) => (
    <Card size="small" style={{ height: '100%', borderTop: `3px solid ${color}` }}>
        <Text type="secondary">{label}</Text>
        <Title level={3} style={{ margin: '8px 0 0' }}>{value}</Title>
    </Card>
);

const JourneyFunnel = () => {
    const pageLayoutProps = usePageLayout();
    const { property, propList, chooseProperty } = pageLayoutProps;
    const [propertyID, setPropertyID] = useState('');
    const [periodKey, setPeriodKey] = useState(getCurrentPeriodKey);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const periodOptions = useMemo(createPeriodOptions, []);

    useEffect(() => {
        const selected = property || propList[0]?.propertyID || '';
        setPropertyID(selected);
    }, [property, propList]);

    const loadReport = useCallback(async () => {
        if (!propertyID) {
            setReport(null);
            return;
        }
        setLoading(true);
        setError('');
        try {
            setReport(await getJourneyFunnel({ propertyID, periodKey }));
        } catch (requestError) {
            setReport(null);
            setError(requestError?.response?.data?.message || 'Could not load calculator journey data.');
            Sentry.captureException(requestError);
        } finally {
            setLoading(false);
        }
    }, [propertyID, periodKey]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    const stageColumns = [
        {
            title: 'Journey stage',
            dataIndex: 'label',
            render: (label) => <Text strong>{label}</Text>,
        },
        {
            title: 'Reached',
            dataIndex: 'reached',
            width: 110,
            align: 'right',
        },
        {
            title: 'Of visits',
            dataIndex: 'percentOfStart',
            width: 220,
            render: (value) => <Progress percent={value || 0} format={() => formatPercent(value)} size="small" />,
        },
        {
            title: 'Of previous stage',
            dataIndex: 'percentOfPrevious',
            width: 150,
            align: 'right',
            render: formatPercent,
        },
        {
            title: 'Stopped here',
            dataIndex: 'leftHere',
            width: 120,
            align: 'right',
        },
    ];
    const stepColumns = [
        { title: 'Step', dataIndex: 'step', width: 100, render: (step) => `Step ${step}` },
        { title: 'Reached', dataIndex: 'reached', width: 120, align: 'right' },
        { title: 'Stopped before calculation', dataIndex: 'leftHere', align: 'right' },
    ];
    const fieldColumns = [
        { title: 'Field', dataIndex: 'label' },
        { title: 'Visits', dataIndex: 'count', width: 100, align: 'right' },
    ];
    const selectedProperty = propList.find((item) => item.propertyID === propertyID);

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{ padding: 24, maxWidth: 1440, margin: '0 auto' }}>
                <Space align="start" style={{ width: '100%', justifyContent: 'space-between', marginBottom: 20 }}>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>Calculator journey</Title>
                        <Text type="secondary">Funnel and drop-off for one property and month.</Text>
                    </div>
                    <Button icon={<ReloadOutlined />} onClick={loadReport} loading={loading} aria-label="Refresh journey report" />
                </Space>

                <Space wrap style={{ marginBottom: 20 }}>
                    <Select
                        aria-label="Property"
                        value={propertyID || undefined}
                        placeholder="Select property"
                        style={{ width: 280 }}
                        options={propList.map((item) => ({ value: item.propertyID, label: item.companyName || item.propertyID }))}
                        onChange={(nextPropertyID) => {
                            setPropertyID(nextPropertyID);
                            chooseProperty?.(nextPropertyID);
                        }}
                    />
                    <Select
                        aria-label="Reporting month"
                        value={periodKey}
                        style={{ width: 210 }}
                        options={periodOptions}
                        onChange={setPeriodKey}
                    />
                    {selectedProperty?.companyName && <Text type="secondary">{selectedProperty.companyName}</Text>}
                </Space>

                {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

                {loading && !report ? (
                    <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>
                ) : !propertyID ? (
                    <Empty description="No property available for this account." />
                ) : !report || report.totalJourneys === 0 ? (
                    <Card><Empty description="No calculator journeys recorded for this month." /></Card>
                ) : (
                    <>
                        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                            <Col xs={24} sm={8}>
                                <FunnelMetric label="Calculator visits" value={report.totalJourneys} color="#1f7a8c" />
                            </Col>
                            <Col xs={24} sm={8}>
                                <FunnelMetric label="Journey events" value={report.journeyEvents} color="#2f9e44" />
                            </Col>
                            <Col xs={24} sm={8}>
                                <FunnelMetric label="Exit signal rate" value={formatPercent(report.exitSignalRate)} color="#d97706" />
                            </Col>
                        </Row>

                        {report.biggestDropOff && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message={`Largest drop: ${report.biggestDropOff.fromLabel} to ${report.biggestDropOff.toLabel}`}
                                description={`${report.biggestDropOff.lost} visits stopped here (${formatPercent(report.biggestDropOff.percentLost)} of the previous stage).`}
                            />
                        )}

                        <Card title="Conversion funnel" style={{ marginBottom: 16 }}>
                            <Table
                                rowKey="stage"
                                columns={stageColumns}
                                dataSource={report.stages || []}
                                pagination={false}
                                size="small"
                                scroll={{ x: 760 }}
                            />
                        </Card>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} lg={12}>
                                <Card title="Form steps">
                                    <Table rowKey="step" columns={stepColumns} dataSource={report.steps || []} pagination={false} size="small" />
                                </Card>
                            </Col>
                            <Col xs={24} lg={12}>
                                <Card title="Form drop-off fields">
                                    <Table rowKey="field" columns={fieldColumns} dataSource={report.formDropOffFields || []} pagination={false} size="small" />
                                </Card>
                            </Col>
                            <Col xs={24}>
                                <Card title="Contact form drop-off fields">
                                    <Table rowKey="field" columns={fieldColumns} dataSource={report.leadFormDropOffFields || []} pagination={false} size="small" />
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}
            </div>
        </PageLayout>
    );
};

export default JourneyFunnel;